import {
  ITEM_PATH,
  ITEM_EDIT_PATH,
  ACCOUNT_SETUP_PATH,
} from 'constants/general';
import { history } from 'store/store';
import {
  StyledTable,
  StyledTableData,
  StyledTableHead,
  StyledTableBody,
} from 'components/Table/Table/Table.styled';
import {
  StyledTableHeader,
  StyledTableHeadRow,
} from 'components/Table/Header/Header.styled';
import { TextInput } from 'components/Inputs/';
import React, { useState, useEffect } from 'react';
import {
  StyledInput,
  StyledLabel,
  StyledWrapper,
  StyledCaption,
  StyledTableRow,
  StyledSubHeading,
  StyledRoundButton,
  StyledTableWrapper,
  StyledErrorMessage,
  StyledButtonWrapper,
  StyledQuantityWrapper,
} from 'components/Item/Condition/Condition.styled';
import { FlexCol, FlexRow } from 'components/Layout/';
import { GOOD, LOST, DAMAGED } from 'constants/inventory';
import { ReactComponent as TickIcon } from 'icons/tick.svg';
import { arrayToSentence, capitalizeFirstLetter } from 'utils/';
import { ReactComponent as EditIcon } from 'icons/edit_circle.svg';
import { CancelButton, DeleteButton, RoundButton } from 'components/Buttons/';

function Condition({
  id,
  stock,
  itemName,
  onSubmit,
  companyId,
  conditions,
  maxQuantity,
  conditionId,
  onModalClose,
  isOnBoarding,
  isUpdatingQuantity,
}) {
  const entityType = 'inventory';
  const hasCondition = !!conditionId;
  const [message, setMessage] = useState('');
  const [rows, setRows] = useState(conditions);
  const [quantity, setQuantity] = useState(stock);
  const [isReadOnly, setIsReadOnly] = useState(hasCondition);

  function onEnableEditing() {
    setIsReadOnly(false);
  }

  /**
   * @param {{
   * currentTarget: {
   * name: string
   * value: string
   * }
   * }} event
   */
  function onInputChange(event) {
    const { currentTarget } = event;
    const { id, name, value } = currentTarget;
    const data = rows.map(function (row) {
      const result = parseInt(value, 10);
      if (String(row.id) === id) {
        if (row.name === GOOD) {
          if (result > quantity) {
            const message = `Good ${itemName} cannot be more than ${quantity}`;
            setMessage(message);
            return row;
          } else {
            setMessage('');
          }
        }
        return { ...row, [name]: result };
      }
      return row;
    });
    setRows(data);
  }

  function onCancel() {
    if (isUpdatingQuantity) {
      onModalClose();
    } else {
      let path;
      if (isOnBoarding) {
        path = ACCOUNT_SETUP_PATH;
      } else {
        path = ITEM_PATH;
        if (id) {
          path = ITEM_EDIT_PATH.replace(':id', id);
        }
      }
      history.push(`${path}#general`);
    }
  }

  /**
   * @param {string} id
   * @param {string} sign
   */
  function onQuantityChange(id, sign) {
    if (sign === '+') {
      const nextRows = rows.map(function (row) {
        if (row.id === id) {
          const { name } = row;
          const qty = row.quantity + 1;
          if ([GOOD, DAMAGED].includes(name)) {
            if (qty > quantity) {
              const label = capitalizeFirstLetter(name);
              const message = `${label} ${itemName} cannot be more than ${quantity}`;
              setMessage(message);
              return row;
            } else {
              setMessage('');
            }
          }
          return { ...row, quantity: qty };
        }
        return row;
      });
      setRows(nextRows);
    } else {
      const nextRows = rows.map(function (row) {
        if (row.id === id) {
          const quantity = row.quantity;
          return { ...row, quantity: quantity > 0 ? quantity - 1 : 0 };
        }
        return row;
      });
      setRows(nextRows);
    }
  }

  function onDeleteCondition() {
    const condition = { id: conditionId };
    onSubmit({ condition, deleting: true });
  }

  function onSaveCondition() {
    let isValid = true;
    const messages = [];
    if (!id) {
      messages.push('Either save or edit an item before adding conditions');
      isValid = false;
    }
    const available = rows.filter(function (row) {
      return row.name !== LOST;
    });
    const total = available.reduce(function (acc, row) {
      return acc + row.quantity;
    }, 0);
    if (total < quantity) {
      const message = `Good + Damaged ${itemName} must equal stock (${quantity})`;
      messages.push(message);
      isValid = false;
    }
    if (!isValid) {
      const message = arrayToSentence({
        array: messages,
        suffix: 'to continue.',
      });
      setMessage(message);
      return;
    } else {
      setMessage('');
    }
    const conditions = rows.reduce(function (acc, row) {
      acc[row.id] = row.quantity;
      return acc;
    }, {});
    if (isUpdatingQuantity) {
      const data = {
        id,
        quantity,
        conditions: { conditions, id: conditionId },
      };
      onSubmit(data);
    } else if (hasCondition) {
      const condition = { conditions, id: conditionId };
      onSubmit({ condition, updating: true });
    } else {
      const condition = {
        conditions,
        entity_id: id,
        company_id: companyId,
        entity_type: entityType,
      };
      onSubmit({ condition, updating: false });
    }
  }

  function onStockChange(e) {
    const { value } = e.currentTarget;
    setQuantity(value ? Number(value) : '');
  }

  useEffect(() => {
    setQuantity(stock);
  }, [stock]);

  useEffect(() => {
    if (conditions) {
      setRows(conditions);
    }
  }, [conditions]);

  useEffect(() => {
    if (hasCondition) {
      setIsReadOnly(hasCondition);
    }
  }, [hasCondition]);

  return (
    <StyledWrapper>
      {isUpdatingQuantity ? (
        <FlexRow>
          <FlexCol flexWidth={6}>
            <TextInput
              isRequired
              name="quantity"
              value={quantity}
              inputId="quantity"
              caption="Quantity"
              inputType="number"
              onChange={onStockChange}
              placeholder="Enter quantity"
              description={`How many pieces do you have (max ${maxQuantity})`}
            />
          </FlexCol>
        </FlexRow>
      ) : (
        <>
          <StyledCaption>Add Quantity</StyledCaption>
          <StyledSubHeading>For items with no attributes</StyledSubHeading>
        </>
      )}
      <StyledTableWrapper>
        <StyledTable>
          <StyledTableHead>
            <StyledTableHeadRow>
              <StyledTableHeader>Condition</StyledTableHeader>
              <StyledTableHeader>Quantity</StyledTableHeader>
            </StyledTableHeadRow>
          </StyledTableHead>
          <StyledTableBody>
            {rows.map(function (row) {
              const { id, name, quantity } = row;
              return (
                <StyledTableRow key={id}>
                  <StyledTableData style={{ width: '15%' }}>
                    <StyledLabel>{capitalizeFirstLetter(name)}</StyledLabel>
                  </StyledTableData>
                  <StyledTableData>
                    <StyledQuantityWrapper>
                      {isReadOnly && !isUpdatingQuantity ? null : (
                        <StyledRoundButton
                          $minus
                          data-testid={`minus-button-${name}`}
                          onClick={onQuantityChange.bind(null, id, '-')}
                        >
                          -
                        </StyledRoundButton>
                      )}
                      <StyledInput
                        id={id}
                        type="number"
                        name="quantity"
                        value={quantity}
                        placeholder="e.g 10"
                        onChange={onInputChange}
                        data-testid={`quantity-${name}`}
                        readOnly={isUpdatingQuantity ? false : isReadOnly}
                      />
                      {isReadOnly && !isUpdatingQuantity ? null : (
                        <StyledRoundButton
                          data-testid={`add-button-${name}`}
                          onClick={onQuantityChange.bind(null, id, '+')}
                        >
                          +
                        </StyledRoundButton>
                      )}
                    </StyledQuantityWrapper>
                  </StyledTableData>
                </StyledTableRow>
              );
            })}
          </StyledTableBody>
        </StyledTable>
      </StyledTableWrapper>
      {message ? (
        <StyledErrorMessage $main aria-live="polite">
          {message}
        </StyledErrorMessage>
      ) : null}
      <FlexRow>
        <StyledButtonWrapper>
          {isReadOnly && !isUpdatingQuantity ? (
            <RoundButton
              caption="Edit"
              flexWidth={100}
              icon={<EditIcon />}
              onClick={onEnableEditing}
            />
          ) : (
            <RoundButton
              flexWidth={100}
              icon={<TickIcon />}
              onClick={onSaveCondition}
              caption={hasCondition ? 'Update' : 'Save'}
            />
          )}
        </StyledButtonWrapper>
        <StyledButtonWrapper>
          <CancelButton onClick={onCancel} />
        </StyledButtonWrapper>
        {hasCondition && !isUpdatingQuantity ? (
          <StyledButtonWrapper>
            <DeleteButton onClick={onDeleteCondition} />
          </StyledButtonWrapper>
        ) : null}
      </FlexRow>
    </StyledWrapper>
  );
}

export default Condition;
